import { useEffect, useState } from "react";
import { PaymentMethodSelect, DEFAULT_PAYMENT_METHOD, paymentMethodLabel } from "@/lib/paymentMethods";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash, CircleNotch, Tag, CurrencyDollar } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };
const KIND_LABELS = { session: "Single sessions", package: "Multi-session packages", membership: "Recurring memberships", gift_card: "Gift cards" };
const KIND_ORDER = ["session", "package", "membership", "gift_card"];
const EMPTY = { name: "", kind: "session", price: "", duration_min: "", sessions_included: "", expires_days: "", equipment: "", capacity_per_day: "", rollover: false, cancel_policy: "credit_back" };

export function ServiceBuilder({ businessId, onChanged }) {
  const [services, setServices] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sellFor, setSellFor] = useState(null);
  const [sellName, setSellName] = useState("");
  const [sellMethod, setSellMethod] = useState(DEFAULT_PAYMENT_METHOD);

  const load = () => api.get(`/businesses/${businessId}/service-catalog`).then((r) => setServices(r.data)).catch(() => setServices([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const num = (v) => (v === "" || v === null ? null : Number(v));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name the service first.");
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/service-catalog`, {
        ...form, price: Number(form.price) || 0, duration_min: num(form.duration_min),
        sessions_included: num(form.sessions_included), expires_days: num(form.expires_days),
        capacity_per_day: num(form.capacity_per_day),
      });
      toast.success("Service added to your menu.");
      setForm(EMPTY); setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the service."); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Remove “${s.name}” from your menu? Existing client packages keep working.`)) return;
    try {
      await api.delete(`/businesses/${businessId}/service-catalog/${s.id}`);
      toast.success(`${s.name} removed from the menu.`);
      load();
    } catch { toast.error("Couldn't remove the service."); }
  };

  const sell = async (svc) => {
    if (!sellName.trim()) return toast.error("Add the client's name.");
    setSaving(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/wellness/purchase`, { service_id: svc.id, client_name: sellName.trim(), payment_method: sellMethod });
      toast.success(data.rowan_message);
      setSellFor(null); setSellName(""); setSellMethod(DEFAULT_PAYMENT_METHOD);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record the sale."); }
    finally { setSaving(false); }
  };

  if (!services) return <p className="text-sm" style={label} data-testid="service-builder-loading">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="service-builder">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm max-w-2xl" style={label}>Build the menu of what you sell — sessions, packages, memberships, gift cards. Rowan handles the revenue and unearned-money accounting behind each one automatically.</p>
        <button onClick={() => setShowForm((s) => !s)} data-testid="new-service-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold shrink-0">
          <Plus size={16} weight="bold" /> New service
        </button>
      </div>

      {showForm && (
        <div className="bk-card p-5 space-y-3" data-testid="service-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <input placeholder="Name (e.g. Hyperbaric Session)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="service-name" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} data-testid="service-kind" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              <option value="session">Single session</option>
              <option value="package">Multi-session package</option>
              <option value="membership">Recurring membership</option>
              <option value="gift_card">Gift card</option>
            </select>
            <input type="number" placeholder="Price $" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="service-price" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input type="number" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} data-testid="service-duration" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            {(form.kind === "package" || form.kind === "membership") && (
              <input type="number" placeholder="Sessions included" value={form.sessions_included} onChange={(e) => setForm((f) => ({ ...f, sessions_included: e.target.value }))} data-testid="service-sessions" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            )}
            <input type="number" placeholder="Expires after (days)" value={form.expires_days} onChange={(e) => setForm((f) => ({ ...f, expires_days: e.target.value }))} data-testid="service-expires" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input placeholder="Equipment / room (e.g. Hyperbaric chamber)" value={form.equipment} onChange={(e) => setForm((f) => ({ ...f, equipment: e.target.value }))} data-testid="service-equipment" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <input type="number" placeholder="Capacity per day (optional)" value={form.capacity_per_day} onChange={(e) => setForm((f) => ({ ...f, capacity_per_day: e.target.value }))} data-testid="service-capacity" className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
            <select value={form.cancel_policy} onChange={(e) => setForm((f) => ({ ...f, cancel_policy: e.target.value }))} data-testid="service-cancel-policy" className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
              <option value="credit_back">Cancelled booking → session credited back</option>
              <option value="forfeit">Cancelled booking → session forfeited</option>
            </select>
          </div>
          {(form.kind === "package" || form.kind === "membership") && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--bk-text)" }}>
              <input type="checkbox" checked={form.rollover} onChange={(e) => setForm((f) => ({ ...f, rollover: e.target.checked }))} data-testid="service-rollover" />
              Unused sessions roll over at renewal
            </label>
          )}
          <div className="flex justify-end">
            <button onClick={create} disabled={saving} data-testid="save-service-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-70">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />} Add to menu
            </button>
          </div>
        </div>
      )}

      {services.length === 0 && (
        <div className="bk-card p-8 text-center" data-testid="service-empty">
          <Tag size={36} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
          <p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>No services yet</p>
          <p className="text-sm mt-1" style={label}>Add your first session, package, or membership to start selling.</p>
        </div>
      )}

      {KIND_ORDER.map((kind) => {
        const rows = services.filter((s) => s.kind === kind);
        if (!rows.length) return null;
        return (
          <div key={kind}>
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "var(--bk-accent)" }}>{KIND_LABELS[kind]}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.map((s) => (
                <div key={s.id} className="bk-card p-4" data-testid={`service-card-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{s.name}</p>
                      <p className="text-xs mt-0.5" style={label}>
                        ${Number(s.price).toLocaleString()}
                        {s.duration_min ? ` · ${s.duration_min} min` : ""}
                        {s.sessions_included ? ` · ${s.sessions_included} sessions` : ""}
                        {s.expires_days ? ` · expires in ${s.expires_days}d` : ""}
                        {s.equipment ? ` · ${s.equipment}` : ""}
                      </p>
                      <p className="text-[11px] mt-1" style={label}>
                        {s.rollover ? "Unused sessions roll over · " : ""}
                        Cancel policy: {s.cancel_policy === "forfeit" ? "session forfeited" : "session credited back"}
                      </p>
                    </div>
                    <button onClick={() => del(s)} data-testid={`service-delete-${s.id}`} className="p-1.5 rounded hover:bg-black/5 shrink-0" style={{ color: "#B23B1F" }} aria-label="Delete service"><Trash size={15} weight="bold" /></button>
                  </div>
                  <div className="mt-3">
                    {sellFor === s.id ? (
                      <div className="space-y-2" data-testid={`service-sell-form-${s.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <input value={sellName} onChange={(e) => setSellName(e.target.value)} placeholder="Client name" data-testid={`service-sell-name-${s.id}`}
                            className="flex-1 min-w-[10rem] rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
                          <PaymentMethodSelect value={sellMethod} onChange={setSellMethod} testId={`service-sell-method-${s.id}`} />
                          <button onClick={() => sell(s)} disabled={saving} data-testid={`service-sell-save-${s.id}`} className="bk-btn-primary px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-50">Record sale</button>
                        </div>
                        <p className="text-[11px]" style={label} data-testid={`service-sell-method-note-${s.id}`}>
                          Paid via <strong>{paymentMethodLabel(sellMethod)}</strong>{sellMethod === "owner_personal" ? " — recorded against the owner's draw; the business is owed this money." : sellMethod === "card_processor" ? " — lands in Merchant Processing Clearing until the payout hits your bank." : sellMethod === "cash" ? " — lands in Cash." : " — lands in Bank."} Held as unearned until sessions are delivered.
                        </p>
                      </div>
                    ) : (
                      <button onClick={() => { setSellFor(s.id); setSellName(""); setSellMethod(DEFAULT_PAYMENT_METHOD); }} data-testid={`service-sell-${s.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-success)" }}>
                        <CurrencyDollar size={14} weight="bold" /> Record a sale
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
